import React from 'react';
import { cn } from '@/lib/utils';

/**
 * The page's numbers as one row, and as the filter control.
 *
 * Twelve pages opened with a grid of metric cards — 96px of white space and
 * shadow to show four numbers you could not do anything with. The same four
 * numbers fit in 34px here, and clicking one filters the list below, so the
 * count stops being decoration and becomes the way you narrow the screen.
 */

const TONES = {
  default: 'text-slate-900',
  blue: 'text-brand-blue',
  accent: 'text-brand-orange',
  good: 'text-emerald-600',
  warn: 'text-amber-600',
  danger: 'text-red-600',
};

export function StatStrip({ stats = [], activeKey, onSelect, children, className, 'data-testid': testId }) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-stretch gap-x-1 gap-y-1 overflow-hidden rounded-lg border border-slate-200 bg-white px-1 py-0.5 shadow-card',
        className
      )}
      data-testid={testId || 'stat-strip'}
    >
      {stats.filter(Boolean).map((stat) => {
        const active = activeKey != null && activeKey === stat.key;
        const clickable = !!onSelect && stat.key != null;
        const Tag = clickable ? 'button' : 'div';
        return (
          <Tag
            key={stat.key || stat.label}
            type={clickable ? 'button' : undefined}
            onClick={clickable ? () => onSelect(active ? null : stat.key) : undefined}
            aria-pressed={clickable ? active : undefined}
            title={stat.title}
            data-testid={stat.key ? `stat-${stat.key}` : undefined}
            className={cn(
              'flex items-baseline gap-1.5 whitespace-nowrap rounded px-2.5 py-2 text-left transition-colors sm:px-2 sm:py-1',
              clickable && 'hover:bg-slate-100',
              active && 'bg-brand-blue/10 ring-1 ring-inset ring-brand-blue/30'
            )}
          >
            <span className={cn('font-heading text-sm font-bold tabular-nums', TONES[stat.tone] || TONES.default)}>
              {stat.value}
            </span>
            <span className="text-xs text-slate-500 sm:text-[11px]">{stat.label}</span>
          </Tag>
        );
      })}
      {children && <div className="ml-auto flex items-center gap-1.5 pr-1">{children}</div>}
    </div>
  );
}

export default StatStrip;
